import React, { useState } from 'react';
import api from '../utils/api';
import Modal from './Modal';
import { LEAD_SOURCES } from '../utils/sources';
import { CASE_CATEGORIES } from '../utils/cases';

// Журнал обращений: быстрая фиксация звонка в 2–3 нажатия (кто бы ни был — взял, передал Саше, отказ, спам)
export const INQUIRY_OUTCOMES = [
  { key: 'taken',   label: 'Взял в работу',       color: '#10b981' },
  { key: 'passed',  label: 'Передал Александру',  color: '#6366f1' },
  { key: 'consult', label: 'Консультация, думает', color: '#f59e0b' },
  { key: 'refused', label: 'Не наш вопрос / отказ', color: '#6b7280' },
  { key: 'spam',    label: 'Спам / продавец',     color: '#dc2626' },
];
export const INQUIRY_OUTCOME_MAP = Object.fromEntries(INQUIRY_OUTCOMES.map(o => [o.key, o]));
export const INQUIRY_TOPICS = [{ key: 'bankruptcy', label: 'Банкротство', color: '#4f6ef7' }, ...CASE_CATEGORIES];
export const INQUIRY_TOPIC_MAP = Object.fromEntries(INQUIRY_TOPICS.map(t => [t.key, t]));
const CHANNELS = ['Звонок', 'Мессенджер', 'Лично', 'Форма на сайте'];

const chip = (on, color = '#4f6ef7') => ({
  padding: '5px 10px', borderRadius: 14, fontSize: 12, cursor: 'pointer',
  border: `1px solid ${on ? color : '#e2e8f0'}`, background: on ? color + '1f' : '#fff',
  color: on ? color : '#334155', fontWeight: on ? 600 : 400,
});
const Row = ({ label, children }) => (
  <div style={{ marginBottom: 12 }}>
    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>{label}</div>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{children}</div>
  </div>
);

const empty = () => ({ channel: 'Звонок', source: '', topic: '', outcome: '', name: '', phone: '', note: '', inquiry_date: new Date().toISOString().slice(0, 10) });

// edit — запись журнала для правки (✏ на странице «Обращения» и в «Аналитике дел»)
const fromRow = r => ({ ...empty(), ...Object.fromEntries(Object.keys(empty()).map(k => [k, r[k] ?? ''])),
  inquiry_date: String(r.inquiry_date || '').slice(0, 10) || empty().inquiry_date });

const kb = n => (n > 1048576 ? (n / 1048576).toFixed(1) + ' МБ' : Math.max(1, Math.round(n / 1024)) + ' КБ');
const ACCEPT = 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.rtf,.odt,audio/*';

export default function QuickInquiry({ onClose, onSaved, edit }) {
  const [f, setF] = useState(() => edit ? fromRow(edit) : empty());
  const [files, setFiles] = useState(() => edit?.files || []); // уже прикреплённые (при правке)
  const [pending, setPending] = useState([]);                  // выбранные/вставленные, загрузятся при сохранении
  const [drag, setDrag] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(0);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const toggle = (k, v) => setF(x => ({ ...x, [k]: x[k] === v ? '' : v })); // повторное нажатие на чип снимает выбор
  const addFiles = list => { const a = Array.from(list || []); if (a.length) setPending(p => [...p, ...a]); };

  // Ctrl+V скриншота/файла в поле текста — прикрепляется файлом; обычный текст вставляется как текст
  const onPaste = e => {
    const fl = Array.from(e.clipboardData?.files || []);
    if (fl.length && !e.clipboardData.getData('text')) { e.preventDefault(); addFiles(fl); }
  };

  const save = async (more) => {
    if (!f.outcome) return;
    setSaving(true);
    try {
      const { data } = edit ? await api.put('/inquiries/' + edit.id, f) : await api.post('/inquiries', f);
      if (pending.length) {
        const fd = new FormData();
        pending.forEach(x => fd.append('files', x, x.name || 'вставка.png'));
        try { await api.post(`/inquiries/${data.id}/files`, fd); }
        catch (e) { alert('Обращение сохранено, но файлы не загрузились: ' + (e.response?.data?.error || 'ошибка')); }
      }
      onSaved && onSaved();
      if (more) { setF(empty()); setPending([]); setDone(n => n + 1); } else onClose();
    } catch (e) { alert(e.response?.data?.error || 'Не сохранилось'); }
    setSaving(false);
  };

  const delFile = async x => {
    if (!window.confirm(`Удалить файл «${x.original_name}»?`)) return;
    await api.delete('/inquiries/files/' + x.id);
    setFiles(a => a.filter(y => y.id !== x.id));
    onSaved && onSaved();
  };

  return (
    <Modal title={edit ? `✏ Обращение №${edit.id}` : '📞 Обращение'} onClose={onClose}
      footer={<>
        {done > 0 && <span style={{ fontSize: 12, color: 'var(--text-muted)', marginRight: 'auto' }}>записано: {done}</span>}
        {!edit && <button className="btn btn-secondary" onClick={() => save(true)} disabled={saving || !f.outcome}>Записать и ещё</button>}
        <button className="btn btn-primary" onClick={() => save(false)} disabled={saving || !f.outcome}>{saving ? 'Сохраняю…' : edit ? 'Сохранить' : 'Записать'}</button>
      </>}>
      <Row label="Итог *">
        {INQUIRY_OUTCOMES.map(o => <button key={o.key} style={chip(f.outcome === o.key, o.color)} onClick={() => toggle('outcome', o.key)}>{o.label}</button>)}
      </Row>
      <Row label="Откуда узнал">
        {LEAD_SOURCES.map(s => <button key={s} style={chip(f.source === s)} onClick={() => toggle('source', s)}>{s}</button>)}
      </Row>
      <Row label="Вопрос">
        {INQUIRY_TOPICS.map(t => <button key={t.key} style={chip(f.topic === t.key, t.color)} onClick={() => toggle('topic', t.key)}>{t.label}</button>)}
      </Row>
      <Row label="Как связался">
        {CHANNELS.map(c => <button key={c} style={chip(f.channel === c)} onClick={() => toggle('channel', c)}>{c}</button>)}
      </Row>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 6, marginBottom: 6 }}>
        <input className="form-input" placeholder="Имя (не обязательно)" value={f.name} onChange={e => set('name', e.target.value)} />
        <input className="form-input" placeholder="Телефон" value={f.phone} onChange={e => set('phone', e.target.value)} />
        <input className="form-input" type="date" value={f.inquiry_date} onChange={e => set('inquiry_date', e.target.value)} />
      </div>
      <div onDragOver={e => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={e => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
        style={{ borderRadius: 8, outline: drag ? '2px dashed #4f6ef7' : 'none', outlineOffset: 2 }}>
        <textarea className="form-textarea" rows={edit ? 8 : 4} style={{ width: '100%', resize: 'vertical' }} value={f.note}
          placeholder="Заметка или текст переписки — можно вставить целиком (Ctrl+V). Скриншот из буфера и файлы тоже вставляются/перетаскиваются сюда"
          onChange={e => set('note', e.target.value)} onPaste={onPaste} />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginTop: 6, fontSize: 12 }}>
          <label className="btn btn-sm btn-secondary" style={{ cursor: 'pointer' }}>
            📎 Прикрепить файлы
            <input type="file" multiple accept={ACCEPT} style={{ display: 'none' }} onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
          </label>
          {files.map(x => (
            <span key={x.id} style={{ padding: '3px 8px', borderRadius: 12, background: '#f1f5f9' }}>
              <a href={`/api/inquiries/files/${x.id}`} target="_blank" rel="noreferrer">{x.original_name}</a>
              <button className="btn-icon" title="Удалить файл" onClick={() => delFile(x)}>✕</button>
            </span>
          ))}
          {pending.map((x, i) => (
            <span key={'p' + i} style={{ padding: '3px 8px', borderRadius: 12, background: '#4f6ef714' }} title="Загрузится при сохранении">
              {x.name || 'вставка.png'} · {kb(x.size)}
              <button className="btn-icon" title="Убрать" onClick={() => setPending(p => p.filter((_, j) => j !== i))}>✕</button>
            </span>
          ))}
        </div>
      </div>
    </Modal>
  );
}
